import { useState, useEffect, createContext, useContext } from 'react';
import { loadThemeConfig, saveThemeConfig, defaultThemeConfig, ThemeConfig } from '@/lib/themeConfig';

interface ThemeContextValue {
  theme: ThemeConfig;
  updateTheme: (partial: Partial<ThemeConfig>) => Promise<void>;
  loading: boolean;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultThemeConfig,
  updateTheme: async () => {},
  loading: true,
});

export function useTheme() {
  return useContext(ThemeContext);
}

export function useThemeProvider() {
  const [theme, setTheme] = useState<ThemeConfig>(() => {
    // loadThemeConfig is synchronous — load immediately
    const saved = loadThemeConfig();
    if (saved) return deepMerge(defaultThemeConfig, saved) as ThemeConfig;
    return defaultThemeConfig;
  });
  const [loading] = useState(false);

  useEffect(() => {
    // no-op: theme is loaded synchronously above
  }, []);

  const updateTheme = async (partial: Partial<ThemeConfig>) => {
    const next = { ...theme, ...partial };
    setTheme(next);
    await saveThemeConfig(next);
  };

  return { theme, updateTheme, loading };
}

function deepMerge(target: unknown, source: unknown): unknown {
  if (typeof target !== 'object' || target === null) return source;
  if (typeof source !== 'object' || source === null) return source;
  if (Array.isArray(source)) return source;
  const result = { ...(target as Record<string, unknown>) };
  for (const key of Object.keys(source as Record<string, unknown>)) {
    const sv = (source as Record<string, unknown>)[key];
    const tv = (target as Record<string, unknown>)[key];
    result[key] = deepMerge(tv, sv);
  }
  return result;
}
